import { useState, useRef, useCallback, useEffect } from 'react';
import { EntryTake, SoulEntry } from '../types';
import { deleteEntryTake, updateTakeTiming, updateTakeVolume } from '../services/entryService';
import { sanitizeTakes } from '../components/studio/studioUtils';
import { TelemetryEntry } from '../components/studio/TelemetryTerminal';

export interface UseStudioTakesProps {
  initialTakes?: EntryTake[];
  takesRef?: React.MutableRefObject<EntryTake[]>;
  duration: number;
  activeEntry: SoulEntry | null;
  setActiveEntry: React.Dispatch<React.SetStateAction<SoulEntry | null>>;
  onEntryUpdated?: (entry: SoulEntry) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  takeAuditionAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeGainRef: React.RefObject<GainNode | null>;
  activePlayingTakeIdRef: React.MutableRefObject<string | null>;
  activeTakeSourceNodeRef: React.MutableRefObject<AudioBufferSourceNode | null>;
  takeBuffersRef: React.MutableRefObject<Map<string, AudioBuffer>>;
  currentTime: number;
  applyVocalMuting: (curTime: number, force?: boolean) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  syncPlayback: (play: boolean) => void;
  addTelemetryLog: (msg: string, type?: TelemetryEntry['type']) => void;
}

export interface UseStudioTakesReturn {
  takes: EntryTake[];
  setTakes: React.Dispatch<React.SetStateAction<EntryTake[]>>;
  takesRef: React.MutableRefObject<EntryTake[]>;
  activeAuditionTakeId: string | null;
  setActiveAuditionTakeId: (id: string | null) => void;
  handleNudgeTakeSync: (takeId: string, deltaSec: number) => void;
  handleNudgeTakeTrim: (takeId: string, type: 'start' | 'end', delta: number) => void;
  handleUpdateTakeTiming: (takeId: string, newStart: number, newEnd: number) => void;
  handleUpdateTakeVolume: (takeId: string, newVolume: number) => void;
  handleSetActiveTake: (lineId: string, takeId: string) => void;
  handleToggleActiveTake: (lineId: string, takeId: string) => void;
  handleDeleteTake: (takeId: string) => Promise<void>;
  handleAuditionTake: (take: EntryTake) => void;
}

export function useStudioTakes({
  initialTakes = [],
  takesRef: externalTakesRef,
  duration,
  activeEntry,
  setActiveEntry,
  onEntryUpdated,
  videoRef,
  takeAuditionAudioRef,
  takeAudioRef,
  takeGainRef,
  activePlayingTakeIdRef,
  activeTakeSourceNodeRef,
  takeBuffersRef,
  currentTime,
  applyVocalMuting,
  setIsPlaying,
  syncPlayback,
  addTelemetryLog
}: UseStudioTakesProps): UseStudioTakesReturn {
  const localTakesRef = useRef<EntryTake[]>([]);
  const takesRef = externalTakesRef || localTakesRef;

  const [takes, setTakes] = useState<EntryTake[]>(() => {
    const init = sanitizeTakes(initialTakes);
    takesRef.current = init;
    return init;
  });
  takesRef.current = takes;

  // Synchronize when entry takes are fetched or refreshed asynchronously
  useEffect(() => {
    if (initialTakes && initialTakes.length > 0) {
      const sanitized = sanitizeTakes(initialTakes);
      setTakes(sanitized);
      takesRef.current = sanitized;
    } else if (!activeEntry) {
      setTakes([]);
      takesRef.current = [];
      setActiveAuditionTakeId(null);
    }
  }, [initialTakes, activeEntry?.id]);

  const [activeAuditionTakeId, setActiveAuditionTakeId] = useState<string | null>(null);

  // Handle take nudge / sync adjustment
  const handleNudgeTakeSync = useCallback(
    (takeId: string, deltaSec: number) => {
      const target = takesRef.current.find((t) => t.id === takeId);
      if (target?.isLocked) {
        addTelemetryLog('Partner take is locked in Colab mode', 'warn');
        return;
      }
      setTakes((prev) => {
        const updated = prev.map((t) => {
          if (t.id === takeId) {
            const newStart = Math.round((t.lineStart + deltaSec) * 100) / 100;
            const newEnd = Math.round((t.lineEnd + deltaSec) * 100) / 100;
            const rawStart = Math.round(((t.rawStart !== undefined ? t.rawStart : t.lineStart) + deltaSec) * 100) / 100;
            const rawEnd = Math.round(((t.rawEnd !== undefined ? t.rawEnd : t.lineEnd) + deltaSec) * 100) / 100;
            return { ...t, lineStart: newStart, lineEnd: newEnd, rawStart, rawEnd };
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
    },
    [activeEntry, applyVocalMuting, currentTime, onEntryUpdated, setActiveEntry, videoRef, addTelemetryLog]
  );

  const handleNudgeTakeTrim = useCallback(
    (takeId: string, type: 'start' | 'end', delta: number) => {
      const target = takesRef.current.find((t) => t.id === takeId);
      if (target?.isLocked) {
        addTelemetryLog('Partner take is locked in Colab mode', 'warn');
        return;
      }
      setTakes((prev) => {
        const updated = prev.map((t) => {
          if (t.id === takeId) {
            const rawStart = t.rawStart !== undefined ? t.rawStart : t.lineStart;
            const rawEnd = t.rawEnd !== undefined ? t.rawEnd : t.lineEnd;
            if (type === 'start') {
              const newStart = Math.min(t.lineEnd - 0.2, Math.round((t.lineStart + delta) * 100) / 100);
              return { ...t, lineStart: newStart, rawStart, rawEnd };
            } else {
              const newEnd = Math.max(t.lineStart + 0.2, Math.round((t.lineEnd + delta) * 100) / 100);
              return { ...t, lineEnd: newEnd, rawStart, rawEnd };
            }
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
    },
    [activeEntry, applyVocalMuting, currentTime, onEntryUpdated, setActiveEntry, videoRef, addTelemetryLog]
  );

  const handleUpdateTakeTiming = useCallback(
    (takeId: string, newStart: number, newEnd: number) => {
      const target = takesRef.current.find((t) => t.id === takeId);
      if (target?.isLocked) return;
      const cleanStart = Math.round(newStart * 100) / 100;
      const cleanEnd = Math.round(newEnd * 100) / 100;

      setTakes((prev) => {
        const updated = prev.map((t) => {
          if (t.id === takeId) {
            const rawStart = t.rawStart !== undefined ? t.rawStart : t.lineStart;
            const rawEnd = t.rawEnd !== undefined ? t.rawEnd : t.lineEnd;
            return { ...t, lineStart: cleanStart, lineEnd: cleanEnd, rawStart, rawEnd };
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
      if (takeId && !takeId.startsWith('take-')) {
        updateTakeTiming(takeId, cleanStart, cleanEnd).catch(() => {});
      }
    },
    [activeEntry, applyVocalMuting, currentTime, onEntryUpdated, setActiveEntry, videoRef]
  );

  const handleUpdateTakeVolume = useCallback(
    (takeId: string, newVolume: number) => {
      const clampedVol = Math.max(0, Math.min(1.5, Math.round(newVolume * 100) / 100));

      setTakes((prev) => {
        const updated = prev.map((t) => {
          if (t.id === takeId) {
            return { ...t, volume: clampedVol };
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });

      // Real-time audio response if this take is currently playing
      if (activePlayingTakeIdRef.current === takeId) {
        if (takeGainRef.current) {
          takeGainRef.current.gain.value = clampedVol;
        }
        if (takeAudioRef.current) {
          takeAudioRef.current.volume = Math.min(1.0, clampedVol);
        }
      }
      // Also adjust audition volume in real time if currently auditioning this take
      if (activeAuditionTakeId === takeId && takeAuditionAudioRef.current) {
        takeAuditionAudioRef.current.volume = Math.min(1.0, clampedVol);
      }

      if (takeId && !takeId.startsWith('take-')) {
        updateTakeVolume(takeId, clampedVol).catch(() => {});
      }
    },
    [
      activeAuditionTakeId,
      activeEntry,
      activePlayingTakeIdRef,
      onEntryUpdated,
      setActiveEntry,
      takeAudioRef,
      takeAuditionAudioRef,
      takeGainRef
    ]
  );

  const handleSetActiveTake = useCallback(
    (lineId: string, takeId: string) => {
      setTakes((prev) => {
        const updated = prev.map((t) => {
          const matchesLine = t.lineIds?.includes(lineId);
          if (matchesLine) {
            return { ...t, isActive: t.id === takeId };
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
      addTelemetryLog(`Take set as active for playback`, 'info');
    },
    [activeEntry, applyVocalMuting, currentTime, onEntryUpdated, setActiveEntry, videoRef, addTelemetryLog]
  );

  // Toggle take active or deactivate (allowing 0 active takes on a line for clean original vocals)
  const handleToggleActiveTake = useCallback(
    (lineId: string, takeId: string) => {
      let isNowActive = false;
      setTakes((prev) => {
        const targetTake = prev.find((t) => t.id === takeId);
        if (targetTake?.isLocked) {
          addTelemetryLog('Partner take is locked in Colab mode', 'warn');
          return prev;
        }
        isNowActive = !targetTake?.isActive;
        const updated = prev.map((t) => {
          const matchesLine = t.lineIds?.includes(lineId);
          if (matchesLine) {
            if (t.id === takeId) {
              return { ...t, isActive: isNowActive };
            }
            return { ...t, isActive: false };
          }
          return t;
        });
        const sanitized = sanitizeTakes(updated);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry = { ...activeEntry, takes: sanitized };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
      addTelemetryLog(isNowActive ? 'Take activated for playback' : 'Take deactivated (original vocal restored)', 'info');
    },
    [activeEntry, applyVocalMuting, currentTime, onEntryUpdated, setActiveEntry, videoRef, addTelemetryLog]
  );

  const handleDeleteTake = useCallback(
    async (takeId: string) => {
      const target = takesRef.current.find((t) => t.id === takeId);
      if (target?.isLocked) {
        addTelemetryLog('Cannot delete partner take in Colab mode', 'warn');
        return;
      }
      // 1. Stop audition audio if deleting the currently auditioned take
      if (activeAuditionTakeId === takeId) {
        if (takeAuditionAudioRef.current) {
          takeAuditionAudioRef.current.pause();
          takeAuditionAudioRef.current.currentTime = 0;
        }
        setActiveAuditionTakeId(null);
      }
      if (activePlayingTakeIdRef.current === takeId) {
        if (activeTakeSourceNodeRef.current) {
          try {
            activeTakeSourceNodeRef.current.stop();
          } catch (_) {}
          activeTakeSourceNodeRef.current = null;
        }
        if (takeAudioRef.current) {
          takeAudioRef.current.pause();
        }
        activePlayingTakeIdRef.current = null;
      }

      takeBuffersRef.current.delete(takeId);

      // 2. Remove locally immediately (optimistic UI update)
      setTakes((prev) => {
        const targetTake = prev.find((t) => t.id === takeId);
        const remaining = prev.filter((t) => t.id !== takeId);

        let finalRemaining = remaining;
        // If the deleted take was active, and there are other takes for this line, make the latest one active
        if (targetTake?.isActive) {
          const targetLineIds = targetTake.lineIds || [];
          for (const lineId of targetLineIds) {
            const remainingForLine = remaining.filter((t) => t.lineIds?.includes(lineId));
            if (remainingForLine.length > 0 && !remainingForLine.some((t) => t.isActive)) {
              const latestForLine = remainingForLine.sort((a, b) => (b.takeNumber || 0) - (a.takeNumber || 0))[0];
              finalRemaining = finalRemaining.map((t) => (t.id === latestForLine.id ? { ...t, isActive: true } : t));
            }
          }
        }
        const sanitized = sanitizeTakes(finalRemaining);
        takesRef.current = sanitized;
        if (activeEntry) {
          const updatedEntry: SoulEntry = {
            ...activeEntry,
            takes: sanitized,
            takeCount: sanitized.length,
            updatedAt: Date.now()
          };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
        }
        return sanitized;
      });

      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);

      if (activeEntry) {
        // Call API in background
        try {
          await deleteEntryTake(takeId, activeEntry.id);
          addTelemetryLog(`Deleted take successfully`, 'info');
        } catch (err: any) {
          console.warn('Backend take deletion note:', err);
        }
      } else {
        addTelemetryLog(`Take removed`, 'info');
      }
    },
    [
      activeAuditionTakeId,
      activeEntry,
      activePlayingTakeIdRef,
      activeTakeSourceNodeRef,
      applyVocalMuting,
      currentTime,
      onEntryUpdated,
      setActiveEntry,
      takeAudioRef,
      takeAuditionAudioRef,
      takeBuffersRef,
      videoRef,
      addTelemetryLog
    ]
  );

  const handleAuditionTake = useCallback(
    (take: EntryTake) => {
      if (activeAuditionTakeId === take.id) {
        // Toggle off
        if (takeAuditionAudioRef.current) {
          takeAuditionAudioRef.current.pause();
          takeAuditionAudioRef.current.currentTime = 0;
        }
        setActiveAuditionTakeId(null);
        return;
      }

      // Stop main scene video if playing
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
        setIsPlaying(false);
        syncPlayback(false);
      }

      setActiveAuditionTakeId(take.id);

      if (takeAuditionAudioRef.current) {
        takeAuditionAudioRef.current.src = take.audioUrl;
        takeAuditionAudioRef.current.currentTime = 0;
        takeAuditionAudioRef.current.volume = Math.min(1.0, take.volume ?? 1.0);
        takeAuditionAudioRef.current.play().catch((err) => {
          console.warn('Take audition error:', err);
          addTelemetryLog(`Could not play take: ${err.message}`, 'warn');
          setActiveAuditionTakeId(null);
        });
      }
    },
    [activeAuditionTakeId, setIsPlaying, syncPlayback, takeAuditionAudioRef, videoRef, addTelemetryLog]
  );

  return {
    takes,
    setTakes,
    takesRef,
    activeAuditionTakeId,
    setActiveAuditionTakeId,
    handleNudgeTakeSync,
    handleNudgeTakeTrim,
    handleUpdateTakeTiming,
    handleUpdateTakeVolume,
    handleSetActiveTake,
    handleToggleActiveTake,
    handleDeleteTake,
    handleAuditionTake
  };
}
